/**
 * ORDERS — Settings → Orders and the ORDER popup (OD-052, owner 20.10.2026; Codex art/src/orders-2026-10-20, approved
 * "Laitetaan"). "Asetuksissa … yksi kohta tilaukset … luettelo viimeisimmistä tilauksista … päivämäärä, mitä on tilattu …
 * paljonko maksoi. … siitä kun klikkaa, niin … kuinka paljon palautusaikaa on jäljellä … paljonko palautettaisiin."
 *
 * The list is the Terms screen's own frame (the notif-cut panel, its title lane, the drawn x and foot pill, the reading
 * surface with the game's ScrollRail); every row is its own element (OD-053). A row opens the shared popup, whose content is
 * a vertical flow of whole sentences — it grows with them, nothing is placed line by line. REFUND sends the withdrawal to
 * Prio1 Pay through the server, which freezes that order's unspent credits; the money goes back on the tenth day, and until
 * then CANCEL REFUND gives the credits back.
 */
import { useEffect, useRef, useState } from 'preact/hooks';
import { art } from '../art';
import { Ink, type InkBox } from '../ink';
import { ScrollRail } from './ScrollRail';
import { PopupPanel } from './PopupPanel';
import LIVE from '../layouts/notif-cut-2026-09-15/live.json';
import { sheet, showToast, frozenOrders } from '../state/store';
import { shopOrders, withdrawOrder, cancelWithdrawal, frozenView, ApiError, type OrderView } from '../net/api';
import { sfx } from '../audio/sfx';

type B = { x: number; y: number; w: number; h: number };
type LiveBox = { x: number; y: number; w: number; h: number; lane: B };
const L = LIVE as Record<string, LiveBox>;
const lv = (id: string, text: string, w: number): InkBox => { const b = L[id]; if (!b) throw new Error(`orders live text missing: ${id}`); const cx = b.x + b.w / 2; return { x: cx - w / 2, y: b.y, w, h: b.h, text, rw: b.w }; };
const hs = (b: B) => ({ left: b.x, top: b.y, width: b.w, height: b.h });
/** Codex 02-orders: the panel, the reading surface and its rail, the x and the foot pill (the Terms screen's own geometry) */
const PANEL = { x: 86, y: 504, w: 772, h: 860 };
const READ = { x: 130, y: 648, w: 664, h: 548 };
const RAIL = { x: 800, y: 648, w: 24, h: 548 };
const CLOSE = { x: 714, y: 522, w: 116, h: 116 };
const BACK = { x: 170, y: 1210, w: 600, h: 116 };
const DAY = 86_400_000;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** a date never breaks across lines inside a sentence (no-break spaces) */
const day = (t: number) => { const d = new Date(t); return `${String(d.getUTCDate()).padStart(2, '0')}\u00a0${MONTHS[d.getUTCMonth()]}\u00a0${d.getUTCFullYear()}`; };
const stamp = (t: number) => { const d = new Date(t); return `${day(t)} · ${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')} UTC`; };
const money = (cents: number | null, currency: string | null) => cents === null ? '' : `${!currency || currency.toUpperCase() === 'USD' ? '$' : `${currency.toUpperCase()} `}${(cents / 100).toFixed(2)}`;

export function OrdersSheet() {
  const [orders, setOrders] = useState<OrderView[] | null>(null); const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState<OrderView | null>(null);
  const lane = useRef<HTMLDivElement>(null);
  const load = async () => { setFailed(false); try { setOrders((await shopOrders()).orders); } catch { setFailed(true); } };
  useEffect(() => {
    // a frozen view (/views ?popup=orders&order=open|withdrawing|ended|refunded) shows the fixture and that order's popup
    if (frozenView() && frozenOrders.value) { const list = frozenOrders.value; setOrders(list); const want = new URLSearchParams(location.search).get('order'); const o = list.find((x) => x.state === want); if (o) setOpen(o); return; }
    void load();
  }, []);
  const back = () => { sfx.tap(); sheet.value = 'settings'; };
  const adopt = (o: OrderView) => { setOrders((list) => list?.map((x) => (x.orderId === o.orderId ? o : x)) ?? list); setOpen(o); };

  return (
    <div class="sheet-backdrop orders60" onClick={back} data-testid="orders-sheet" style={{ '--od-divider': `url(${art('/art/lobby-kit/row.divider.png')})` }}>
      <div class="sheet cut43 plate" role="dialog" aria-label="Orders" style={hs(PANEL)} onClick={(e) => e.stopPropagation()}><div class="stage-in" style={{ left: -PANEL.x, top: -PANEL.y }}>
        <img class="cut" src={art('/art/notif-cut/empty/panel.png')} alt="" draggable={false} style={{ ...hs(PANEL), zIndex: 20 }} />
        <Ink box={lv('title', 'ORDERS', 440)} text="ORDERS" class="st-t-title" align="center" maxW={430} z={46} />
        <button class="cut-hit" type="button" data-testid="orders-close" aria-label="Back to settings" style={hs(CLOSE)} onClick={back} />
        <div class="od-read" ref={lane} style={{ ...hs(READ), zIndex: 30 }} data-testid="orders-list">
          {failed ? <div class="od-status"><p>Could not load your orders.</p><button class="od-retry" type="button" data-testid="orders-retry" onClick={() => void load()}>TRY AGAIN</button></div>
            : orders === null ? <div class="od-status"><p>Loading…</p></div>
            : orders.length === 0 ? <div class="od-status"><p>No orders yet.</p></div>
            : orders.map((o) => (
              <button key={o.orderId} class="od-row" type="button" data-testid={`order-${o.orderId}`} aria-label={`${day(o.createdAt)}, ${o.credits} credits, ${money(o.priceCents, o.currency)}`} onClick={() => { sfx.tap(); setOpen(o); }}>
                <span class="od-date">{day(o.createdAt)}</span><span class="od-item">{o.credits} credits</span><span class="od-price">{money(o.priceCents, o.currency)}</span>
              </button>
            ))}
        </div>
        <div class="lg-rail" style={{ ...hs(RAIL), position: 'absolute', zIndex: 31 }}><ScrollRail target={lane} w={RAIL.w} testid="orders-scrollbar" /></div>
        <Ink box={{ ...lv('cta', 'BACK TO SETTINGS', 500), x: 220 }} text="BACK TO SETTINGS" class="nf-t-cta" align="center" maxW={500} z={46} />
        <button class="cut-hit" type="button" data-testid="orders-back" aria-label="Back to settings" style={hs(BACK)} onClick={back} />
      </div></div>
      {open && <OrderPopup o={open} onClose={() => setOpen(null)} onChange={adopt} />}
    </div>
  );
}

/** ORDER: the shared popup, one wrapping element per sentence (Codex flowTemplate order-popup-vertical-flow-v1) */
function OrderPopup({ o, onClose, onChange }: { o: OrderView; onClose: () => void; onChange: (o: OrderView) => void }) {
  const [busy, setBusy] = useState(false); const [err, setErr] = useState('');
  const w = o.withdrawal;
  const daysLeft = Math.max(1, Math.ceil((o.windowEndsAt - Date.now()) / DAY));
  const act = async (call: () => Promise<{ order: OrderView }>, what: string) => {
    if (busy) return; setBusy(true); setErr(''); sfx.select();
    try { onChange((await call()).order); }
    catch (e) {
      const code = e instanceof ApiError ? e.code : '';
      setErr(code === 'WITHDRAWAL_PERIOD_ENDED' ? 'The withdrawal period has ended.' : code === 'NOTHING_TO_REFUND' ? 'No refundable credits remain.' : code === 'REFUND_ALREADY_PAID' ? 'The refund is already being paid.' : `Could not ${what}. Please try again.`);
    } finally { setBusy(false); }
  };
  const close = () => { sfx.tap(); onClose(); };
  return (
    <div class="sheet-backdrop od-scrim" onClick={(e) => { e.stopPropagation(); close(); }} data-testid="order-popup-backdrop">
      <PopupPanel title="ORDER" label="Order" onClose={close} closeTestid="order-close" testid={`order-popup-${o.state}`} cls="od-pop">
        <div class="od-flow">
          <p class="od-meta od-first">{stamp(o.createdAt)}</p>
          <p class="od-name">{o.credits} credits</p>
          <p class="od-line">Paid {money(o.priceCents, o.currency)}</p>
          <p class="od-cap">Order number</p>
          <p class="od-meta od-tight">{o.orderId}</p>
          {o.state === 'open' && <>
            <p class="od-line od-lead od-narrow">You can withdraw for {daysLeft} more {daysLeft === 1 ? 'day' : 'days'}.</p>
            <p class="od-soft">Refundable now:</p>
            <p class="od-strong od-tight">{o.refundableCredits} credits · {money(o.refundCents, o.currency)}</p>
            {o.refundableCredits > 0 ? <>
              <p class="od-soft">The refund is paid 10 days after you withdraw.</p>
              {o.pauses && <p class="od-soft">Buying credits is then paused for one month.</p>}
            </> : <p class="od-soft">No refundable credits remain.</p>}
            {err && <p class="od-line" data-testid="order-error">{err}</p>}
            <button class="od-btn od-gold" type="button" data-testid="order-refund" aria-disabled={busy || o.refundableCredits <= 0 || undefined}
              style={{ '--plate': `url(${art('/art/lobby-kit/cta.plate.png')})` }} onClick={busy || o.refundableCredits <= 0 ? undefined : () => void act(() => withdrawOrder(o.orderId), 'withdraw')}>
              <span>{busy ? 'REFUNDING…' : 'REFUND'}</span>
            </button>
          </>}
          {o.state === 'withdrawing' && w && <>
            <p class="od-line od-lead">Refund in progress.</p>
            <p class="od-soft">You get {money(w.refundCents, w.currency)} back by {day(w.refundBy)} at the latest.</p>
            <p class="od-soft">{w.credits} credits were taken back from your account.</p>
            {w.pausedUntil !== null && w.pausedUntil > Date.now() && <p class="od-soft">Buying credits is paused until {day(w.pausedUntil)}.</p>}
            {err && <p class="od-line" data-testid="order-error">{err}</p>}
            {w.status === 'pending' && <button class="od-btn od-navy" type="button" data-testid="order-cancel-refund" aria-disabled={busy || undefined}
              style={{ '--plate': `url(${art('/art/ui-extra/navy-pill.png')})` }} onClick={busy ? undefined : () => void act(async () => { const r = await cancelWithdrawal(o.orderId); showToast('Refund cancelled — the credits are back'); return r; }, 'cancel the refund')}>
              <span>CANCEL REFUND</span>
            </button>}
          </>}
          {o.state === 'ended' && <p class="od-line od-result od-narrow">The withdrawal period has ended.</p>}
          {o.state === 'refunded' && <p class="od-line od-result">Refunded on {day(w?.doneAt ?? o.createdAt)}.</p>}
        </div>
      </PopupPanel>
    </div>
  );
}

